/**
 * The title and description of every fixed page on the site, in one place.
 *
 * ## Why this is not written where each page is rendered
 *
 * It used to be. Each component called `usePageMeta` with its own literals, which is the obvious
 * arrangement and worked for exactly as long as nothing else needed to know the answer.
 *
 * Two other things need it. The build pre-renders a real HTML file per route, because a crawler
 * that does not run JavaScript sees only what the server sent — and what it saw was one identical
 * `<title>` and one `<link rel="canonical">` pointing at the home page, on every URL. And the
 * sitemap needs the list of pages that exist. Three copies of a page title is three chances for two
 * of them to disagree, and the copy a customer sees first is the one in the search result, which is
 * the copy nobody looks at while working.
 *
 * So the metadata lives here, the component reads it, the pre-render reads it, and the sitemap is
 * generated from it. One edit, three correct outputs.
 *
 * ## Descriptions are written to be read
 *
 * A description is the two lines under the blue link. It is advertising copy with a length limit,
 * not a keyword field: roughly 140 to 160 characters, saying what the page is for in the words
 * somebody would use to ask for it. Over about 160 Google truncates it mid-sentence.
 */

export interface SitePage {
  /** The route, exactly as the router declares it, always with a leading slash. */
  path: string;
  /**
   * The page's own name, without the site name — `Pricing` becomes `Pricing · Techie Gain`. Also
   * the name in its breadcrumb trail.
   *
   * `null` only on the home page, whose title is the positioning line rather than a section name.
   */
  title: string | null;
  /**
   * The `<title>` when it should say more than the page's name, for the words people search with.
   * Kept short enough that the full title, with the site name, fits a result line.
   */
  seoTitle?: string;
  description: string;
  /**
   * Sitemap weight, 0 to 1. Relative only: it says which of our own pages matters more, and says
   * nothing to a search engine about how we compare to anybody else.
   */
  priority: number;
  /** Kept out of the index, and out of the sitemap. Utility pages and dead ends. */
  noindex?: boolean;
}

/**
 * Keyed rather than a bare array so a component naming a page it does not have fails to compile
 * instead of rendering with the wrong description.
 */
export const PAGES = {
  home: {
    path: '/',
    title: null,
    description:
      'Automate, monitor and back up Linux servers from one platform: safe rollouts, security hardening, Auto-Fix and restore-tested backups. 7-day free trial.',
    priority: 1,
  },

  pricing: {
    path: '/pricing',
    title: 'Pricing',
    seoTitle: 'Pricing: Plans from $12 a Month',
    description:
      'Starter $12, Pro $29, Business $49 a month, two months free yearly, and a 7-day free trial with no card. Every plan includes automation, monitoring and backups.',
    priority: 0.9,
  },

  features: {
    path: '/features',
    title: 'Features',
    seoTitle: 'Features: Automation, Monitoring and Backups',
    description:
      'Linux server playbooks, security hardening, command runner, server templates, safe rollouts, run history, Auto-Fix, backups, restore testing and team access.',
    priority: 0.8,
  },

  blog: {
    path: '/blog',
    title: 'Blog',
    seoTitle: 'Blog: Backups, Monitoring, Server Automation',
    description:
      'Practical guides to Linux server backups, monitoring, SSL renewals, SSH hardening and patching, written by the team that builds Techie Gain.',
    priority: 0.8,
  },

  docs: {
    path: '/docs',
    title: 'Documentation',
    description:
      'Step-by-step guides to start a free trial, connect Linux servers, run playbooks and commands, harden SSH, use templates, set up monitoring and back up servers.',
    priority: 0.9,
  },

  docsPlaybooks: {
    path: '/docs/playbooks',
    title: 'Playbooks',
    seoTitle: 'Linux Server Playbooks: Every Option Explained',
    description:
      'What each playbook does on Ubuntu, Debian and RHEL servers, every option explained, and what to do when a run reports an error.',
    priority: 0.8,
  },

  docsTemplates: {
    path: '/docs/templates',
    title: 'Server Templates',
    seoTitle: 'Server Templates: What Each One Builds',
    description:
      'WordPress, Laravel, Node.js, Python, Docker, Kubernetes, CI runner and HTTPS reverse proxy templates: their steps, options and next steps.',
    priority: 0.8,
  },

  docsTroubleshooting: {
    path: '/docs/troubleshooting',
    title: 'Troubleshooting',
    seoTitle: 'Common Errors and How to Fix Them',
    description:
      'What the platform\'s error messages mean and how to fix them: server connections, playbook runs, monitoring alerts, backups and billing.',
    priority: 0.8,
  },

  monitoringGuides: {
    path: '/guides',
    title: 'Monitoring Guides',
    seoTitle: 'Website, API, SSL and DNS Monitoring Guides',
    description:
      'Guides to website, API, SSL certificate, DNS, domain expiry and port monitoring: what each check detects and how to configure it for your servers.',
    priority: 0.7,
  },

  contact: {
    path: '/contact',
    title: 'Contact',
    description:
      'Contact Techie Gain with questions about the platform, pricing or your infrastructure. Existing customers can open a support ticket from the dashboard.',
    priority: 0.4,
  },

  // ── The account doors ──────────────────────────────────────────────────────────────────────
  // Indexed on purpose. Somebody searching the product name to get back to their own account is
  // one of the most common brand queries there is, and the page they want is this one. What must
  // not happen is the untitled version of it: Google will write its own title from whatever text
  // it finds, and "Welcome back" on the sign-in form became the result heading "back".

  login: {
    path: '/login',
    title: 'Log In',
    description:
      'Log in to your Techie Gain account to manage automation, monitoring, Auto-Fix and backups for your Linux servers.',
    priority: 0.5,
  },

  register: {
    path: '/register',
    title: 'Sign Up',
    description:
      'Create a Techie Gain account and start a 7-day free trial with no card. Automate, monitor and back up Linux servers from any provider over SSH, with no agent.',
    priority: 0.6,
  },

  privacy: {
    path: '/privacy',
    title: 'Privacy Policy',
    description:
      'How Techie Gain collects, uses and protects your information, including the credentials for your servers and your backup storage.',
    priority: 0.3,
  },

  terms: {
    path: '/terms',
    title: 'Terms of Service',
    description:
      'The terms for using Techie Gain to automate, monitor and back up your servers: accounts, acceptable use, billing and refunds, availability and termination.',
    priority: 0.3,
  },

  // ── Not for the index ──────────────────────────────────────────────────────────────────────

  staffLogin: {
    path: '/admin/login',
    title: 'Staff Log In',
    description: 'Log in for Techie Gain staff.',
    priority: 0,
    noindex: true,
  },

  notFound: {
    path: '/404',
    title: 'Page not found',
    description: 'That page does not exist.',
    priority: 0,
    noindex: true,
  },
} satisfies Record<string, SitePage>;

/** Every fixed page, for the pre-render and the sitemap. */
export const ALL_PAGES: SitePage[] = Object.values(PAGES);
